<template>
  <div class="custom-phone-input-container">
    <div class="bordered-bottom">
      <label
        v-if="displayLabel"
        for="tel"
      >{{ label }}</label>
      <div class="form-element-container">
        <!-- phone prefix Picker -->
        <Field
          :ref="`${name}-code-country`"
          v-slot="{ field }"
          v-model="mutatedCodeCountryValue"
          validateOnInput
          class="form-element-contain"
          :label="label"
          :name="`${name}-code-country`"
          :rules="[`phoneNumberWithoutCountryCode:@${name}-phone-number`].join('|')"
        >
          <v-select
            id="form-code-country"
            v-bind="field"
            ref="vSelect"
            v-model="mutatedCodeCountryValue"
            class="form-phone-input"
            :clearable="true"
            :model-value="field.value"
            :options="selectOptions"
            :get-option-key="getOptionKey"
            :disabled="isReadOnly"
            :placeholder="`${$t('form.areaCode')} ${validation.split('|').includes('required') ? '*' : ''}`"
            @open="openSelect()"
            @close="closeSelect()"
            @option:selected="onChange"
          >
            <template #no-options="{ searching }">
              <template v-if="searching">
                {{ $t('errors.datas_not_found') }}
              </template>
            </template>
          </v-select>
        </Field>
        <transition name="slideleft">
          <FullScreenSelect
            v-if="showSelectorFS"
            :defaultValue="mutatedCodeCountryValue"
            :options="selectOptions"
            :name="`${$t('form.areaCode')} ${validation.split('|').includes('required') ? '*' : ''}`"
            :labelFields="null"
            :searchbar="true"
            :parentType="'CustomPhoneInput'"
            @fs-select-close=";(showSelectorFS = false), focusPhoneInput()"
            @selected-option="onChange"
          />
        </transition>

        <!-- phone number input -->
        <Field
          :ref="`${name}-phone-number`"
          v-slot="{ field }"
          v-model="mutatedPhoneNumberValue"
          validateOnInput
          tag="div"
          class="form-element-contain"
          :label="label"
          :name="`${name}-phone-number`"
          :rules="[`checkValidPhoneNumber:@${name}-code-country`, validation].join('|')"
          @update:model-value="onInput('phone-number')"
        >
          <div id="tel-contain">
            <input
              id="tel"
              ref="tel-input"
              type="tel"
              :model-value="field.value"
              data-vv-validate-on="blur"
              v-bind="field"
              maxlength="15"
              :placeholder="`${placeholder} ${validation.split('|').includes('required') ? '*' : ''}`"
              :readonly="isReadOnly || isDisplayErrorPhone"
            >
            <img
              v-if="![null, undefined, ''].includes(mutatedPhoneNumberValue)"
              class="form-generator-clear-button"
              src="@/assets/icons/cancel.svg"
              alt="clear input"
              @click="clearInput"
            >
          </div>
        </Field>
      </div>
    </div>
    <ErrorMessage
      class="error-msg"
      :name="`${name}-phone-number`"
    />
    <span
      v-if="isDisplayErrorPhone"
      class="error-msg"
    >{{ $t('errors.phone_empty_address') }}</span>
  </div>
</template>

<script>
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { Field, ErrorMessage } from 'vee-validate'
import selectOptions from '@/data/countries-en-GB.json'
import countriesJson from '@/mixins/CombinedCountries.js'
import { store } from '@/stores/index.js'
import { mapState } from 'pinia'
import FullScreenSelect from '@/components/GenericComponents/Form/FullScreenSelect.vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

/**
 * Props:
 * codeCountryValue: default telephone prefix
 * phoneNumberValue: default phone number
 * name: name of the input
 */
export default {
  name: 'CustomPhoneInput',
  components: { Field, ErrorMessage, FullScreenSelect, vSelect },
  expose: ['clearInput', 'clearCodeCountry'],
  props: {
    label: String,
    validation: {
      type: [String, Boolean],
      default: null
    },
    placeholder: String,
    codeCountryValue: String,
    phoneNumberValue: [String, Number, Object],
    defaultValue: {
      type: [String, Array, Object],
      default: null
    },
    name: String,
    displayLabel: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isDisplayErrorPhone: {
      type: Boolean,
      default: false
    },
    displayRawPhoneNumber: {
      type: Boolean,
      default: false
    },
    explodeNumber: {
      type: Boolean,
      default: false
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedCodeCountryValue: null,
      mutatedPhoneNumberValue: ' ',
      selectOptions,
      showSelectorFS: false
    }
  },
  computed: {
    ...mapState(store, ['deviceType'])
  },
  // Load json on lang change
  watch: {
    '$i18n.locale'() {
      this.setCountryJson(this.$i18n.locale)
    }
  },
  async mounted() {
    const countryJson = await this.setCountryJson(this.$i18n.locale)
    const parsedNumber = this.parsePhoneNumber(this.defaultValue)

    if (this.defaultValue && parsedNumber) {
      this.mutatedPhoneNumberValue = parsedNumber.nationalNumber
    } else if (
      this.defaultValue &&
      (this.defaultValue.includes('+') || this.defaultValue.toLowerCase() === 'autocomplete')
    ) {
      this.mutatedPhoneNumberValue = null
      // Test if phone match the format "(something)" and explode the string to get the phone number value
    } else if (this.defaultValue && this.defaultValue.toString().match(/^((.+))$/i)?.length > 0) {
      const values = this.defaultValue.split(')')
      const valuesIsAnArray = Array.isArray(values) && values.length > 1
      this.mutatedPhoneNumberValue = valuesIsAnArray ? values[1] : this.defaultValue
    } else {
      this.mutatedPhoneNumberValue = this.defaultValue
    }
    this.mutatedCodeCountryValue = this.setDefaultCode(countryJson, this.defaultValue)

    if (
      !this.mutatedCodeCountryValue?.dial_code ||
      this.mutatedCodeCountryValue.dial_code === '' ||
      !this.mutatedPhoneNumberValue ||
      this.mutatedPhoneNumberValue === ''
    ) {
      this.$emit('addData', { value: '', name: this.name })
    } else {
      let countryCode = this.mutatedCodeCountryValue.dial_code
      if (this.mutatedCodeCountryValue === null) {
        countryCode = ''
      }

      let ret = { value: countryCode + this.mutatedPhoneNumberValue, name: this.name }
      if (this.explodeNumber) {
        ret.value = {
          dial_code: countryCode,
          number: this.mutatedPhoneNumberValue
        }
        if (parsedNumber?.countryCallingCode && parsedNumber.nationalNumber) {
          ret.value.dial_code = parsedNumber.countryCallingCode
          ret.value.number = parsedNumber.nationalNumber
        }
      }
      this.$emit('addData', ret)
    }
  },
  methods: {
    async setCountryJson(lang) {
      let options = []
      const countriesData = countriesJson[lang.replace('-', '_')]
      for (const country of countriesData) {
        options.push({
          label: `${country.name} (${country.dial_code})`,
          name: country.name,
          dial_code: country.dial_code,
          code: country.code
        })
        if (country.additional_dial_codes) {
          let i = 1
          for (const additionalDialCode of country.additional_dial_codes) {
            options.push({
              label: `${country.name} (${additionalDialCode})`,
              name: country.name,
              dial_code: additionalDialCode,
              code: country.code + i
            })
            i++
          }
        }
      }
      this.selectOptions = options
      return options
    },
    setDefaultCode(countryJson, defaultValue) {
      if (defaultValue === this.mutatedPhoneNumberValue) {
        return null
      } else if (defaultValue === 'autocomplete') {
        return null
      } else if (defaultValue && !this.parsePhoneNumber(defaultValue)) {
        let countryDefault = null
        // capture value between () in phone number to set the country code. example: (FR) should match France
        if (defaultValue.toString().match(/^((.+))$/i)?.length > 0) {
          let values = defaultValue.split(')')
          if (Array.isArray(values) && values.length > 0) {
            values = values[0].replace('(', '')
            for (const country of countryJson) {
              if ([country.code, country.name, country.dial_code].includes(values)) {
                countryDefault = country
                break
              }
            }
          }
        }
        return countryDefault
      } else if (defaultValue && this.parsePhoneNumber(defaultValue)) {
        const parsedNumber = this.parsePhoneNumber(defaultValue)
        return countryJson.find(country => country.code === parsedNumber.country)
      } else {
        return defaultValue === null || defaultValue === '' ? null : {}
      }
    },
    onInput() {
      if (
        !this.mutatedCodeCountryValue?.dial_code ||
        this.mutatedCodeCountryValue.dial_code === '' ||
        !this.mutatedPhoneNumberValue ||
        this.mutatedPhoneNumberValue === ''
      ) {
        this.$emit('addData', { value: '', name: this.name })
      } else {
        let parsedNumber = this.parsePhoneNumber(
          this.mutatedCodeCountryValue.dial_code + this.mutatedPhoneNumberValue
        )
        if (parsedNumber) {
          let phoneNumber = ''
          if (this.explodeNumber) {
            phoneNumber = {
              dial_code: parsedNumber.countryCallingCode,
              number: parsedNumber.nationalNumber
            }
          } else {
            phoneNumber = parsedNumber.countryCallingCode + parsedNumber.nationalNumber
          }
          this.$emit('addData', {
            value: phoneNumber,
            name: this.name
          })
        }
      }
    },
    closeSelect() {
      if (this.deviceType === 'desktop') {
        this.focusPhoneInput()
      }
    },
    focusPhoneInput() {
      this.$refs['tel-input'].focus()
    },
    // Separate country code and number
    parsePhoneNumber(number) {
      if (this.explodeNumber && number && typeof number === 'object' && !Array.isArray(number)) {
        number = number.dial_code + '' + number.number
      }
      let phoneNumber = this.cleanUpPhoneNumber(number)
      // If phone number has no country code add the default value
      phoneNumber =
        phoneNumber[0] !== '+' && this.defaultValue && this.defaultValue[0] === '+'
          ? this.defaultValue + phoneNumber
          : phoneNumber
      const result = parsePhoneNumberFromString(phoneNumber)
      if (result) {
        return {
          ...result,
          countryCallingCode: '+' + result.countryCallingCode
        }
      } else {
        return null
      }
    },
    // Remove characters that are not numbers from phone numbers
    cleanUpPhoneNumber(number) {
      if (!number) {
        return ''
      }
      return number
        .split('')
        .map(character => {
          if ('0123456789+'.includes(character)) {
            return character
          }
        })
        .join('')
    },
    // If DisplayRawPhoneNumber props is true, display the phone even if parsePhoneNumber doesn't work
    haveToDisplayRawPhoneNumber() {
      if (this.displayRawPhoneNumber) {
        return this.defaultValue
      } else {
        return null
      }
    },
    getOptionKey(data) {
      return data?.code || ''
    },
    clearInput() {
      this.mutatedPhoneNumberValue = ''
      this.onInput()
    },
    clearCodeCountry() {
      this.mutatedCodeCountryValue = null
      this.onInput()
    },
    onChange(value) {
      this.mutatedCodeCountryValue = value
      this.onInput('code-country')
    },
    openSelect() {
      if (this.deviceType !== 'desktop') {
        document.activeElement.blur()
        this.showSelectorFS = true
      }
    }
  }
}
</script>

<style lang="scss">
  .form-phone-input .vs__dropdown-menu {
    width: 90vw !important;
    overflow-x: hidden !important;
  }

  .form-element-container {
    #form-code-country {
      .vs__dropdown-toggle {
        border: none;

        .vs__selected-options {
          flex-wrap: nowrap;
        }
      }
    }

    .vs__clear,
    .vs__open-indicator {
      fill: transparent;
    }

    .vs__open-indicator {
      background-image: url('@/assets/icons/arrow-down.svg');
      background-position: center right;
      background-origin: content-box;
      background-repeat: no-repeat;
      background-size: contain;
      transform-origin: center;
      width: 0.9rem;
      height: 0.9rem;
    }

    .vs__clear {
      background-image: url('@/assets/icons/cancel.svg');
      background-position: center right;
      background-origin: content-box;
      background-repeat: no-repeat;
      background-size: contain;
      width: 0.8rem;
      height: 0.8rem;
    }
  }
</style>

<style lang="scss" scoped>
  .custom-phone-input-container {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    margin: 10px 0;

    /* Tablet (portrait) */
    @media only screen and (width >= 768px) {
      margin: 20px 0;
    }

    /* Desktops and laptops */
    @media only screen and (width >= 1024px) {
      margin: 20px 0;
    }

    /* Large screens */
    @media only screen and (width >= 1824px) {
      margin: 20px 0;
    }

    & > div {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      white-space: nowrap;
      align-items: center;

      label {
        font-weight: bolder;
        margin-right: 1rem;
      }
    }

    .form-element-container {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      #form-code-country,
      #tel-contain {
        width: 50%;
      }

      select,
      input {
        height: 100%;
        border: none;
      }

      input {
        border: none;
        width: 90%;
      }

      select {
        appearance: none;
        width: 100%;
        text-indent: 4px;
        text-overflow: '';
        padding-right: 4px;
        background-image: url('@/assets/icons/arrow-select.png');
        background-position: center right;
        background-origin: content-box;
        background-repeat: no-repeat;
        background-size: contain;
        color: rgb(var(--secondary-color));
      }
    }

    .error-msg {
      white-space: normal !important;
      justify-content: center;
    }
  }
</style>
